import 'unfetch/polyfill';
import { service, factories, models } from 'powerbi-client';

import { refreshDelaySeconds } from './powerbi.report.refresh';

// Lazily constructed so pages with no Power BI report never pay the cost of
// instantiating the SDK's postMessage proxy.
let powerbiServiceInstance = null;
function getPowerbiService() {
    if (!powerbiServiceInstance) {
        powerbiServiceInstance = new service.Service(
            factories.hpmFactory, factories.wpmpFactory, factories.routerFactory
        );
    }
    return powerbiServiceInstance;
}

// Dedupes token requests when the same report is embedded twice on one page.
const inflightTokenRequests = new Map();

function fetchToken(tokenUrl) {
    if (inflightTokenRequests.has(tokenUrl)) {
        return inflightTokenRequests.get(tokenUrl);
    }

    const request = fetch(tokenUrl, { credentials: 'same-origin' })
        .then((response) => {
            if (!response.ok) {
                throw new Error(`Power BI embed token request failed with status ${ response.status }`);
            }
            return response.json();
        })
        .finally(() => {
            inflightTokenRequests.delete(tokenUrl);
        });

    inflightTokenRequests.set(tokenUrl, request);
    return request;
}

function renderError(container) {
    container.textContent = 'This report could not be loaded.';
}

function scheduleRefresh(report, tokenUrl, expiresIn) {
    const delay = refreshDelaySeconds(expiresIn) * 1000;

    setTimeout(() => {
        fetchToken(tokenUrl)
            .then(({ token, expires_in: nextExpiresIn }) => report.setAccessToken(token)
                .then(() => scheduleRefresh(report, tokenUrl, nextExpiresIn)))
            // expiresIn of 0 always maps to MIN_DELAY_S via refreshDelaySeconds.
            .catch(() => scheduleRefresh(report, tokenUrl, 0));
    }, delay);
}

function embedContainer(container) {
    const { embedUrl, reportId, tokenUrl } = container.dataset;

    // Phased embedding: the iframe warms up while the token request is in
    // flight, so the async fetch doesn't add to perceived load time.
    getPowerbiService().bootstrap(container, { type: 'report', embedUrl });

    fetchToken(tokenUrl)
        .then(({ token, expires_in: expiresIn }) => {
            const report = getPowerbiService().embed(container, {
                type: 'report',
                id: reportId,
                embedUrl,
                accessToken: token,
                tokenType: models.TokenType.Embed,
                settings: {
                    panes: {
                        filters: {
                            expanded: false
                        },
                        pageNavigation: {
                            visible: false
                        }
                    }
                }
            });

            report.on('error', () => renderError(container));

            scheduleRefresh(report, tokenUrl, expiresIn);
        })
        .catch(() => renderError(container));
}

function init() {
    const containers = [].slice.call(document.querySelectorAll('[data-powerbi-report]'));
    containers.forEach(embedContainer);
}

export default { init };
